/**
 * Workspace Management Action Definitions
 *
 * Actions for saving, restoring, and clearing workspace state.
 *
 * @module gibbs/registry/actions/workspace
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── capture-state ──────────────────────────────────────────────────────────

export const captureState = {
    id: 'capture-state',
    name: 'Save Workspace State',
    category: 'Workspace Management',
    description: 'Serialize the entire workspace state (loaded proteins, compounds, display settings, compound heritage, energies, custom properties) into a restorable snapshot.',
    inputs: [
        param('workspace', 'object', 'Current workspace (default: active)', false),
    ],
    outputs: [
        param('savedState', T.WorkspaceState, 'Complete serialized state'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('workspace_has_data', 'Workspace must have loaded data'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'CaptureState',
    serverCommand: null,
    jobType: null,
};

// ─── capture-state-to-string ────────────────────────────────────────────────

export const captureStateToString = {
    id: 'capture-state-to-string',
    name: 'Export Workspace as JSON',
    category: 'Workspace Management',
    description: 'Serialize workspace state as a JSON string for saving to file or transmission.',
    inputs: [
        param('workspace', 'object', 'Current workspace', false),
    ],
    outputs: [
        param('json', 'string', 'JSON-formatted workspace state'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('workspace_has_data', 'Workspace must have loaded data'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'CaptureStateToString',
    serverCommand: null,
    jobType: null,
};

// ─── restore-state ──────────────────────────────────────────────────────────

export const restoreState = {
    id: 'restore-state',
    name: 'Restore Workspace State',
    category: 'Workspace Management',
    description: 'Restore a workspace from a saved state snapshot. Reloads proteins, compounds, display settings, energies, and properties. Handles multiple restore scenarios (PDB files, map cases, folding results, etc.).',
    inputs: [
        param('savedState', T.WorkspaceState, 'Previously captured state'),
    ],
    outputs: [
        param('workspace', 'object', 'Restored workspace'),
    ],
    parameters: [
        param('alert', 'boolean', 'Show error alerts', false, { defaultValue: true }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [
        post('protein_loaded', 'Proteins from the saved state are loaded'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'RestoreState',
    serverCommand: null,
    jobType: null,
};

// ─── zap-all ────────────────────────────────────────────────────────────────

export const zapAll = {
    id: 'zap-all',
    name: 'Clear Workspace',
    category: 'Workspace Management',
    description: 'Remove all loaded proteins, compounds, and data from the workspace. Nuclear reset.',
    inputs: [],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ZapAll',
    serverCommand: 'zap-all',
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    captureState,
    captureStateToString,
    restoreState,
    zapAll,
];
